<template>
  <tr>
    <td>
      <span>
        <i
          v-if="isLoggedUser"
          class="icon-user"
        ></i>
        <span v-if="user">{{ user.display_name }}</span>
        <template v-else-if="group">
          <i
            :class="group.public ? 'icon-globe' : 'icon-lock'"
            :title="`This group is ${group.public ? 'public' : 'private'}`"
          ></i>
          <router-link :to="{ name: 'group-manage', params: { groupId: group.id } }">{{
            group.name
          }}</router-link>
        </template>
        <span v-else>—</span>
      </span>
    </td>
    <td>
      <template v-if="user">{{ user.email }}</template>
      <ItemId
        v-else-if="group"
        :item-id="group.id"
      />
      <template v-else>—</template>
    </td>
    <td>
      <form
        v-if="editing"
        v-on:submit.prevent="isLoggedUser ? (updateModal = true) : updateLevel()"
      >
        <div class="field has-addons">
          <div class="control">
            <div class="select">
              <select
                v-model="level"
                :disabled="loading || undefined"
                required
              >
                <option
                  v-for="r in ROLES"
                  :key="r.value"
                  :value="r.value"
                  :title="r.help"
                >
                  {{ r.display }}
                </option>
              </select>
            </div>
          </div>
          <div class="control">
            <button
              class="button is-primary"
              type="submit"
              :class="{ 'is-loading': loading }"
              :disabled="loading || undefined"
            >
              <i class="icon-edit"></i>
            </button>
          </div>
        </div>
      </form>
      <div v-else>
        <RoleTag
          v-if="role"
          :role="role"
        />
        <span v-if="!role">—</span>
        <a
          v-if="isAdmin && !loading"
          v-on:click="edit"
          class="has-text-info"
        >
          <i class="icon-edit"></i>
        </a>
      </div>
    </td>
    <td>
      <!-- delete member -->
      <button
        type="button"
        class="button has-text-danger"
        :class="{ 'is-loading': loading }"
        :disabled="!deletionEnabled || undefined"
        v-on:click="deleteModal = deletionEnabled"
        :title="deletionTitle"
      >
        <span v-if="isLoggedUser">Leave</span>
        <i
          v-else
          class="icon-trash"
        ></i>
      </button>
    </td>
    <Modal
      v-model="deleteModal"
      title="Delete access right"
    >
      <span>
        Are you sure you want to revoke
        <template v-if="isLoggedUser">your own</template>
        <template v-else>{{ displayName }}'s</template>
        access right?
      </span>
      <br />
      <span>This action is irreversible.</span>
      <template v-slot:footer="{ close }">
        <button
          type="button"
          class="button"
          v-on:click="close"
        >
          Cancel
        </button>
        <button
          type="button"
          autofocus
          class="button is-danger"
          :class="{ 'is-loading': deleteLoading }"
          v-on:click="revokeAccess"
        >
          Delete
        </button>
      </template>
    </Modal>
    <Modal
      v-model="updateModal"
      title="Update your own access right"
    >
      <p>Are you sure you want to update your own access right to {{ selectedRole?.display }}?</p>
      <template v-slot:footer="{ close }">
        <button
          type="button"
          class="button"
          v-on:click="close"
        >
          Cancel
        </button>
        <button
          type="button"
          autofocus
          v-on:click="updateLevel"
          class="button is-info"
          :class="{ 'is-loading': updateLoading }"
        >
          Update
        </button>
      </template>
    </Modal>
  </tr>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import { deleteMembership, updateMembership } from '@/api'
import ItemId from '@/components/ItemId.vue'
import Modal from '@/components/Modal.vue'
import { ROLES } from '@/config'
import { errorParser, getRole } from '@/helpers'
import { useAuthStore, useNotificationStore } from '@/stores'
import type { Membership } from '@/types'

import RoleTag from './RoleTag.vue'

export default defineComponent({
  components: {
    ItemId,
    RoleTag,
    Modal,
  },
  emits: ['update'],
  props: {
    // Generic membership containing a user xor a group
    member: {
      type: Object as PropType<Membership>,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    ROLES,
    loading: false,
    editing: false,
    level: ROLES.guest.value,
    deleteModal: false,
    deleteLoading: false,
    updateModal: false,
    updateLoading: false,
  }),
  computed: {
    ...mapState(useAuthStore, { loggedUser: 'user' }),
    user() {
      return this.member.user
    },
    group() {
      return this.member.group
    },
    isLoggedUser(): boolean {
      return !!this.loggedUser && this.user !== null && this.user.email === this.loggedUser?.email
    },
    role() {
      return getRole(this.member.level)
    },
    displayName(): string {
      if (this.group) return `group ${this.group.name}`
      return `user ${this.user?.display_name}`
    },
    deletionEnabled(): boolean {
      return this.isLoggedUser || (!this.loading && this.isAdmin)
    },
    deletionTitle(): string {
      if (!this.deletionEnabled) return 'Admin right is required to remove a member'
      return this.isLoggedUser ? 'Revoke your membership' : 'Remove this member'
    },
    selectedRole() {
      return getRole(this.level)
    },
  },
  methods: {
    ...mapActions(useNotificationStore, ['notify']),
    edit() {
      this.level = this.member.level
      this.editing = true
    },
    async updateLevel() {
      if (!this.editing || this.loading) return

      this.updateLoading = true
      try {
        await updateMembership({ id: this.member.id, level: this.level })
        // Send success notification
        this.notify({
          type: 'success',
          text: 'The member access level was successfully updated.',
        })
        // update store membersPage
        this.$emit('update')
      } catch (err) {
        this.notify({ type: 'error', text: errorParser(err) })
      } finally {
        this.updateLoading = false
        this.editing = false
        this.updateModal = false
      }
    },
    async revokeAccess() {
      this.deleteLoading = true
      try {
        await deleteMembership(this.member.id)
        this.deleteModal = false
        // update store membersPage
        this.$emit('update')
      } catch (err) {
        this.notify({ type: 'error', text: errorParser(err) })
      } finally {
        this.deleteLoading = false
      }
    },
  },
})
</script>
